import React from 'react';
import { 
  HomeOutlined,
  ClusterOutlined,
  NodeIndexOutlined,
  DatabaseOutlined
} from '@ant-design/icons';
import { BioIcon } from '../components/icons/BioIcons';

export interface MenuItem {
  key: string;
  icon: React.ReactNode;
  label: string;
  path: string;
  description?: string;
  color?: string;
  category?: string;
  apiCount?: number;
  toolCount?: number;
  modelCount?: number;
}

export interface ServiceCard {
  title: string;
  icon: React.ReactNode;
  description: string;
  link: string;
  color: string;
  category: string;
}

// 创建图标映射
const iconWrapper = (icon: React.ReactNode) => (
  <span className="anticon" style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}>
    {icon}
  </span>
);

const createIcon = (iconName: string) => {
  const iconMap: { [key: string]: React.ReactNode } = {
    'HomeOutlined': <HomeOutlined />,
    'ClusterOutlined': <ClusterOutlined />,
    'NodeIndexOutlined': <NodeIndexOutlined />,
    'DatabaseOutlined': <DatabaseOutlined />,
    'dna': iconWrapper(<BioIcon type='dna' />),
    'plasmid': iconWrapper(<BioIcon type='plasmid' />),
    'antibody': iconWrapper(<BioIcon type='antibody' />),
    'protein': iconWrapper(<BioIcon type='protein' />),
    'structure': iconWrapper(<BioIcon type='structure' />)
  };
  return iconMap[iconName] || <HomeOutlined />;
};

// 统一的菜单配置
export const menuConfig: MenuItem[] = [
  {
    key: '/',
    icon: createIcon('HomeOutlined'),
    label: '首页',
    path: '/',
    description: '平台首页，提供各种生物信息学服务概览',
    color: '#1890ff',
    category: 'home'
  },
  {
    key: '/sequence',
    icon: createIcon('dna'),
    label: '核酸序列分析',
    path: '/sequence',
    description: '提供序列比对、翻译、反向互补等功能',
    color: '#722ed1',
    category: 'analysis',
    apiCount: 4,
    toolCount: 3,
    modelCount: 1
  },
  {
    key: '/cloning',
    icon: createIcon('plasmid'),
    label: '分子克隆',
    path: '/cloning',
    description: '提供Sanger测序分析、引物设计、质粒构建绘图、酶切位点分析等功能',
    color: '#13c2c2',
    category: 'analysis',
    apiCount: 4,
    toolCount: 4,
    modelCount: 1
  },
  {
    key: '/protein',
    icon: createIcon('protein'),
    label: '蛋白质分析',
    path: '/protein',
    description: '提供蛋白质序列分析、信号肽预测、跨膜区预测等功能',
    color: '#1890ff',
    category: 'analysis',
    apiCount: 8,
    toolCount: 6,
    modelCount: 3
  },
  {
    key: '/antibody',
    icon: createIcon('antibody'),
    label: '抗体分析',
    path: '/antibody',
    description: '提供抗体CDR区预测、表位预测等分析功能',
    color: '#52c41a',
    category: 'analysis',
    apiCount: 5,
    toolCount: 4,
    modelCount: 2
  },
  {
    key: '/interaction',
    icon: createIcon('NodeIndexOutlined'),
    label: '分子互作分析',
    path: '/interaction',
    description: '提供分子对接、相互作用预测等功能',
    color: '#fa8c16',
    category: 'analysis',
    apiCount: 2,
    toolCount: 2,
    modelCount: 1
  },
  {
    key: '/structure',
    icon: createIcon('structure'),
    label: '结构与设计',
    path: '/structure',
    description: '提供蛋白质结构预测、结构从头设计等功能',
    color: '#f5222d',
    category: 'design',
    apiCount: 7,
    toolCount: 7,
    modelCount: 4
  }
  // 生物数据库查询 - 已禁用，内部使用时取消注释
  // {
  //   key: '/bio_db',
  //   icon: createIcon('DatabaseOutlined'),
  //   label: '生物数据库查询',
  //   path: '/bio_db',
  //   description: '生物数据库查询与搜索',
  //   color: '#722ed1',
  //   category: 'analysis',
  //   apiCount: 4,
  //   toolCount: 3,
  //   modelCount: 1
  // }
];

// 获取服务卡片数据（排除首页）
export const getServiceCards = (): ServiceCard[] => {
  return menuConfig
    .filter(item => item.key !== '/')
    .map(item => ({
      title: item.label,
      icon: item.icon,
      description: item.description || '',
      link: item.path,
      color: item.color || '#1890ff',
      category: item.category || 'analysis'
    }));
};

// 获取统计数据
export const getStatistics = () => {
  const analysisServices = menuConfig.filter(item => item.category === 'analysis').length;
  const totalApiCount = menuConfig.reduce((sum, item) => sum + (item.apiCount || 0), 0);
  const totalToolCount = menuConfig.reduce((sum, item) => sum + (item.toolCount || 0), 0);
  const totalModelCount = menuConfig.reduce((sum, item) => sum + (item.modelCount || 0), 0);

  return {
    analysisServices,
    totalApiCount,
    totalToolCount,
    totalModelCount
  };
};

// 获取菜单项（排除首页）
export const getMenuItems = () => {
  return menuConfig.filter(item => item.key !== '/');
};